import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { UseOutletContextSale } from '../Sale';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Contact } from '../../../../models/contact.model';
import { legendValidInvalidRestart, legendValidInvalidIconRestart } from '../../../../tools/legend.data.entry.tool';
import { Container } from '../../../../styles/container.style';
import { evaluateLegendValidEmptyIcon, evaluateLegendValidRequiredIcon } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { loadLocationBrowser } from '../../../../scripts/app.location.script';

import AppLegend from '../../../../components/element/Legend';
import AppInputCountry from '../../../../components/element/InputCountry';

export interface AppSaleFormDeliveryProps {
  customer: any | Contact,
  street: {value: string, valid: boolean},
  streetNumber: {value: string, valid: boolean},
  additional: {value: string, valid: boolean},
  administrativeArea: {value: string, valid: boolean},
  city: {value: string, valid: boolean},
  zipCode: {value: string, valid: boolean},
  country: {value: string, valid: boolean},
  setStreet: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreetNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdditional: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdministrativeArea: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setZipCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCountry: Dispatch<SetStateAction<{value: string, valid: boolean}>>
};

const AppSaleFormDelivery: React.FunctionComponent<AppSaleFormDeliveryProps> = ({customer, street, streetNumber, additional, administrativeArea, city, zipCode, country, setStreet, setStreetNumber, setAdditional, setAdministrativeArea, setCity, setZipCode, setCountry}) => {
  const {companyForUser} = UseOutletContextSale()
  const {lang} = StateLanguage()

  const [defaultAddress, setDefaultAddress] = useState(false)

  const handleChangeDefaultAddress = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setDefaultAddress(event.target.checked)
    restartForm()

    if (customer && event.target.checked) {
      setStreet({value: customer.data.address.street, valid: true})
      setStreetNumber({value: customer.data.address.number, valid: true})
      setAdditional({value: customer.data.address.additional, valid: true})
      setAdministrativeArea({value: customer.data.address.administrative_area, valid: true})
      setCity({value: customer.data.address.city, valid: true})
      setZipCode({value: customer.data.address.zip_code, valid: true})
      setCountry({value: customer.data.address.country, valid: true})
    } else {
      setStreet({value: '', valid: false})
      setStreetNumber({value: '', valid: false})
      setAdditional({value: '', valid: true})
      setAdministrativeArea({value: '', valid: false})
      setCity({value: '', valid: false})
      setZipCode({value: '', valid: false})
      loadLocationBrowser(setCountry)
    }
  }

  const handleChangeStreet = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreet({...street, value: event.target.value})
  }

  const handleChangeStreetNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({...streetNumber, value: event.target.value})
  }

  const handleChangeAdditional = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdditional({...additional, value: event.target.value})
  }

  const handleChangeAdministrativeArea = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdministrativeArea({...administrativeArea, value: event.target.value})
  }

  const handleChangeCity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCity({...city, value: event.target.value})
  }

  const handleChangeZipCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setZipCode({...zipCode, value: event.target.value})
  }

  const validateStreet = () => {
    evaluateLegendValidRequiredIcon(expressions.street, street, setStreet, 'input-street', 'legend-street-valid', 'legend-street-required')
  }

  const validateStreetNumber = () => {
    evaluateLegendValidRequiredIcon(expressions.streetnumber, streetNumber, setStreetNumber, 'input-street-number', 'legend-street-number-valid', 'legend-street-number-required')
  }

  const validateAdditional = () => {
    evaluateLegendValidEmptyIcon(expressions.text, additional, setAdditional, 'input-additional', 'legend-additional-valid')
  }

  const validateAdministrativeArea = () => {
    evaluateLegendValidRequiredIcon(expressions.location, administrativeArea, setAdministrativeArea, 'input-administrative-area', 'legend-administrative-area-valid', 'legend-administrative-area-required')
  }

  const validateCity = () => {
    evaluateLegendValidRequiredIcon(expressions.location, city, setCity, 'input-city', 'legend-city-valid', 'legend-city-required')
  }

  const validateZipCode = () => {
    evaluateLegendValidRequiredIcon(expressions.zipcode, zipCode, setZipCode, 'input-zip-code', 'legend-zip-code-valid', 'legend-zip-code-required')
  }

  function restartForm() {
    legendValidInvalidIconRestart('input-street', 'legend-street-valid')
    legendValidInvalidIconRestart('input-street', 'legend-street-required')
    legendValidInvalidIconRestart('input-street-number', 'legend-street-number-valid')
    legendValidInvalidIconRestart('input-street-number', 'legend-street-number-required')
    legendValidInvalidIconRestart('input-additional', 'legend-additional-valid')
    legendValidInvalidIconRestart('input-administrative-area', 'legend-administrative-area-valid')
    legendValidInvalidIconRestart('input-administrative-area', 'legend-administrative-area-required')
    legendValidInvalidIconRestart('input-city', 'legend-city-valid')
    legendValidInvalidIconRestart('input-city', 'legend-city-required')
    legendValidInvalidIconRestart('input-zip-code', 'legend-zip-code-valid')
    legendValidInvalidIconRestart('input-zip-code', 'legend-zip-code-required')
    legendValidInvalidRestart('legend-country-required')
  }

  useEffect( () => {
    if (street.valid && street.value.length && streetNumber.valid && streetNumber.value.length && administrativeArea.valid && administrativeArea.value.length && city.valid && city.value.length && zipCode.valid && zipCode.value.length && country.valid && country.value.length) {
      setDefaultAddress(customer && customer.data.address.street === street.value && customer.data.address.number === streetNumber.value && customer.data.address.additional === additional.value && customer.data.address.administrative_area === administrativeArea.value && customer.data.address.city === city.value && customer.data.address.zip_code === zipCode.value && customer.data.address.country === country.value)
    } else {
      if (customer) {
        setDefaultAddress(true)
        setStreet({value: customer.data.address.street, valid: true})
        setStreetNumber({value: customer.data.address.number, valid: true})
        setAdditional({value: customer.data.address.additional, valid: true})
        setAdministrativeArea({value: customer.data.address.administrative_area, valid: true})
        setCity({value: customer.data.address.city, valid: true})
        setZipCode({value: customer.data.address.zip_code, valid: true})
        setCountry({value: customer.data.address.country, valid: true})
      } else {
        setDefaultAddress(false)
        setStreet({value: '', valid: false})
        setStreetNumber({value: '', valid: false})
        setAdditional({value: '', valid: true})
        setAdministrativeArea({value: '', valid: false})
        setCity({value: '', valid: false})
        setZipCode({value: '', valid: false})
        loadLocationBrowser(setCountry)
      }
    }

    restartForm()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, customer])

  return (
    <div className="card card-flush mb-5">
      <div className="card-header">
        <div className="card-title">
          <h4>{lang.labels.delivery}</h4>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="d-flex flex-stack">
          <div className="fw-bold fs-6">{lang.labels.isDeliveryAddressTheSameAsCustomersAddress}</div>
          <div className="form-check form-switch form-check-custom form-check-solid">
            <input className="form-check-input w-50px h-25px" type="checkbox" disabled={!customer} checked={defaultAddress} onChange={handleChangeDefaultAddress} />
            <label className="form-check-label text-muted text-uppercase">{lang.labels.customer}</label>
          </div>
        </div>
        <Container property={(!defaultAddress).toString()}>
          <div className="separator my-3"></div>
          <div className="row mb-0 mb-lg-3">
            <div className="col-8 fv-row">
              <input id="input-street" className="form-control form-control-solid" type="text" autoComplete="off" name="street" placeholder={lang.labels.street.toUpperCase()} value={street.value} onChange={handleChangeStreet} onKeyUp={validateStreet} onBlur={validateStreet} />
              <AppLegend attribute={{check: street.valid, name: "street", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
            </div>
            <div className="col-4 fv-row">
              <input id="input-street-number" className="form-control form-control-solid" type="text" autoComplete="off" name="number" placeholder={lang.labels.number.toUpperCase()} value={streetNumber.value} onChange={handleChangeStreetNumber} onKeyUp={validateStreetNumber} onBlur={validateStreetNumber} />
              <AppLegend attribute={{check: streetNumber.valid, name: "street-number", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          <div className="row mb-0 mb-lg-3">
            <div className="col-12 fv-row">
              <input id="input-additional" className="form-control form-control-solid" type="text" autoComplete="off" name="additional" placeholder={lang.labels.additional.toUpperCase()} value={additional.value} onChange={handleChangeAdditional} onKeyUp={validateAdditional} onBlur={validateAdditional} />
              <AppLegend attribute={{check: additional.valid, name: "additional", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          <div className="row mb-0 mb-lg-3">
            <div className="col-12 fv-row">
              <input id="input-administrative-area" className="form-control form-control-solid" type="text" autoComplete="off" name="administrative-area" placeholder={lang.labels.administrativeArea.toUpperCase()} value={administrativeArea.value} onChange={handleChangeAdministrativeArea} onKeyUp={validateAdministrativeArea} onBlur={validateAdministrativeArea} />
              <AppLegend attribute={{check: administrativeArea.valid, name: "administrative-area", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          <div className="row mb-0 mb-lg-3">
            <div className="col-4 fv-row">
              <input id="input-zip-code" className="form-control form-control-solid" type="text" autoComplete="off" name="zip-code" placeholder={lang.labels.zipCode.toUpperCase()} value={zipCode.value} onChange={handleChangeZipCode} onKeyUp={validateZipCode} onBlur={validateZipCode} />
              <AppLegend attribute={{check: zipCode.valid, name: "zip-code", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
            </div>
            <div className="col-8 fv-row">
              <input id="input-city" className="form-control form-control-solid" type="text" autoComplete="off" name="city" value={city.value} placeholder={lang.labels.city.toUpperCase()} onChange={handleChangeCity} onKeyUp={validateCity} onBlur={validateCity} />
              <AppLegend attribute={{check: city.valid, name: "city", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          <div className="row">
            <div className="col-12 fv-row">
              <AppInputCountry options={{small: false, disabled: false, placeholder: lang.labels.country}} country={country} setCountry={setCountry}></AppInputCountry>
            </div>
          </div>
        </Container>
      </div>
    </div>
  )
};

export default AppSaleFormDelivery;
